import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { btnPrimary, btnOutline, overline } from "@/components/kit";
import { track } from "@/lib/track";
import { ArrowRight, ArrowUpRight, Check, Clock, MapPin, Sparkles } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { CONTACT } from "@/data/site";

const FAQ = [
  { q: "Quel budget prévoir pour une soirée de Noël d'entreprise à Lyon ?", a: "Trois formules selon votre situation : animation seule dès 2 900 € HT, Noël dans vos locaux dès 6 900 € HT, soirée complète avec lieu dès 18 000 € HT. Après votre brief, vous recevez trois pistes chiffrées adaptées à votre effectif." },
  { q: "À quel moment réserver sa soirée de Noël ?", a: "Dès que votre date est connue : les jeudis et vendredis de décembre sont les premiers complets. Nous traitons aussi les demandes tardives selon les disponibilités des lieux et des intervenants." },
  { q: "Pouvez-vous trouver le lieu pour nous ?", a: "Oui. Indiquez simplement dans le formulaire si vous avez déjà un lieu ou si nous devons le chercher : nous proposons des lieux adaptés à votre effectif, à Lyon et dans sa métropole." },
  { q: "Organisez-vous des soirées de Noël hors de Lyon ?", a: "Oui, nous organisons des événements de fin d'année dans toute la France, avec le même interlocuteur unique pour coordonner tous les prestataires." },
];

const SERVICE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Organisation de soirée de Noël d'entreprise à Lyon",
  description: "Organisation clé en main de soirées de Noël d'entreprise à Lyon : lieu, traiteur, décoration, DJ, animations et personnel, coordonnés par un interlocuteur unique.",
  provider: {
    "@type": "LocalBusiness",
    name: "Désir Events",
    url: "https://desir-events.com",
    telephone: CONTACT.phoneFixe,
    email: CONTACT.email,
    address: { "@type": "PostalAddress", streetAddress: "129 rue de Servient", addressLocality: "Lyon", postalCode: "69003", addressCountry: "FR" },
  },
  areaServed: { "@type": "Country", name: "France" },
};

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

const BREADCRUMB_JSONLD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Accueil", item: "https://desir-events.com/" },
    { "@type": "ListItem", position: 2, name: "Noël en entreprise" },
    { "@type": "ListItem", position: 3, name: "Soirée de Noël entreprise Lyon", item: "https://desir-events.com/fete-de-noel-entreprise/lyon" },
  ],
};

const SITUATIONS = [
  { title: "La soirée entre collaborateurs", text: "Le classique de fin d'année : dîner, DJ, animations et danse pour souder les équipes avant les fêtes. Nous trouvons la salle, le traiteur et l'ambiance adaptés à votre effectif." },
  { title: "Le cocktail ou dîner de fin d'année", text: "Un format plus feutré pour marquer l'année : lieu patrimonial, cocktail dînatoire, discours de direction et animations discrètes — mentaliste, musiciens, photobooth." },
  { title: "L'événement salariés et clients", text: "Réunir équipes et clients autour d'un même temps fort : réception premium, protocole soigné, coordination des invités VIP et de la presse si besoin." },
];

const FORMULES = [
  { name: "Animation seule", price: "dès 2 900 € HT", note: "Vous avez déjà le lieu et le traiteur : nous apportons les animations, le DJ, le photobooth et la coordination de soirée." },
  { name: "Noël dans vos locaux", price: "dès 6 900 € HT", note: "Décoration, sapin, animations et traiteur apportés dans vos bureaux ou votre atelier : le format malin sans location de salle." },
  { name: "Soirée complète", price: "dès 18 000 € HT", note: "Lieu privatisé, traiteur, décoration, DJ, technique et personnel : la soirée clé en main, coordonnée par un chef de projet unique." },
];

const LIEUX = [
  "La Sucrière (Confluence)", "Musée des Confluences", "Palais de la Bourse",
  "Grand Hôtel-Dieu", "Péniches et berges de Saône", "Domaines et châteaux de la métropole",
  "Vos propres locaux", "Halle Tony Garnier (très grands effectifs)",
];

const DEROULE = [
  { step: "Cadrage", text: "Date, effectif, ambiance et budget : un échange de 30 minutes suffit pour cadrer." },
  { step: "Proposition", text: "Sous 24 h ouvrées, une première piste avec lieu, formule et budget détaillé poste par poste." },
  { step: "Préparation", text: "Repérage, réservation des intervenants, rétroplanning : votre chef de projet pilote tout." },
  { step: "Jour J", text: "Montage, accueil, coordination et démontage : vous profitez de votre soirée, nous gérons le reste." },
];

export default function NoelLyon() {
  return (
    <>
      <Seo
        title="Soirée de Noël entreprise Lyon | Organisation clé en main"
        description="Organisation de soirée de Noël d'entreprise à Lyon : lieu, traiteur, décoration, DJ, animations et personnel. Trois formules dès 2 900 € HT, devis sous 24 h ouvrées."
        path="/fete-de-noel-entreprise/lyon"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(SERVICE_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB_JSONLD)}</script>
      </Helmet>
      <PageHero eyebrow="Noël · Lyon" title={<>Organisation de votre soirée de Noël<br /><span className="italic text-lime-dim">d'entreprise à Lyon</span></>} />

      {/* Trois situations */}
      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10" data-testid="noel-lyon-situations">
        <Reveal><span className={overline}>Quelle soirée de Noël pour vous ?</span></Reveal>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {SITUATIONS.map((s, i) => (
            <Reveal key={s.title} delay={i}>
              <div className="h-full rounded-2xl border border-ink/10 bg-mist p-7">
                <Sparkles size={18} className="text-lime-dim" />
                <h2 className="mt-4 font-serif text-xl text-ink">{s.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-ink/60">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Formules avec prix de départ */}
      <section className="border-t border-ink/10 bg-white" data-testid="noel-lyon-formules">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Nos formules</span></Reveal>
          <Reveal delay={1}><h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">Trois niveaux, des prix de départ affichés</h2></Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {FORMULES.map((f, i) => (
              <Reveal key={f.name} delay={i}>
                <div className={`h-full rounded-2xl border p-8 ${i === 2 ? "border-lime-dim/50 bg-mist shadow-lg" : "border-ink/10 bg-white"}`} data-testid={`noel-lyon-formule-${i}`}>
                  <p className="font-serif text-2xl text-ink">{f.name}</p>
                  <p className="mt-3 font-serif text-3xl text-lime-dim">{f.price}</p>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{f.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={2}><p className="mt-6 max-w-2xl text-sm text-ink/50">Prix de départ HT, ajustés à votre effectif et à la date. Chaque devis détaille tous les postes : lieu, traiteur, décoration, technique, personnel, coordination.</p></Reveal>
        </div>
      </section>

      {/* Lieux lyonnais */}
      <section className="border-t border-ink/10 bg-mist" data-testid="noel-lyon-lieux">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Où fêter Noël à Lyon</span></Reveal>
          <Reveal delay={1}><h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">Des lieux que nous pratiquons réellement</h2></Reveal>
          <div className="mt-10 grid gap-3 sm:grid-cols-2">
            {LIEUX.map((l) => (
              <div key={l} className="flex items-center gap-3 rounded-xl border border-ink/10 bg-white px-5 py-4">
                <MapPin size={15} className="shrink-0 text-lime-dim" />
                <p className="font-medium text-ink">{l}</p>
              </div>
            ))}
          </div>
          <Reveal delay={2}>
            <p className="mt-6 max-w-2xl text-sm text-ink/50">En décembre, les lieux lyonnais sont pris d'assaut : contactez-nous idéalement dès septembre-octobre pour sécuriser votre date.</p>
          </Reveal>
        </div>
      </section>

      {/* Déroulé */}
      <section className="border-t border-ink/10 bg-white" data-testid="noel-lyon-deroule">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Comment ça se passe</span></Reveal>
          <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-ink/10 md:grid-cols-4">
            {DEROULE.map((d, i) => (
              <Reveal key={d.step} delay={i}>
                <div className="h-full bg-mist p-7">
                  <span className="font-serif text-3xl text-lime-dim">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-serif text-xl text-ink">{d.step}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/60">{d.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-ink/10 bg-mist" data-testid="noel-lyon-faq">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
          <Reveal><span className={overline}>Questions fréquentes</span></Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {FAQ.map((f, i) => (
              <Reveal key={f.q} delay={i % 2}>
                <div className="h-full rounded-2xl border border-ink/10 bg-white p-7" data-testid={`noel-lyon-faq-${i}`}>
                  <h3 className="font-serif text-xl leading-snug text-ink">{f.q}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Maillage */}
      <section className="border-t border-ink/10 bg-white" data-testid="noel-lyon-related">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-10">
          <Reveal><span className={overline}>Autour de Noël</span></Reveal>
          <div className="mt-6 flex flex-wrap gap-3">
            {[
              { label: "Animations de Noël pour entreprises et CSE", to: "/animation-noel-entreprise" },
              { label: "Arbre de Noël CSE à Lyon", to: "/evenements-cse/lyon" },
              { label: "Décoration de Noël pour magasins", to: "/decoration-noel-magasin" },
              { label: "Décoration de Noël en entreprise", to: "/decoration-noel-entreprise" },
              { label: "Soirée d'entreprise à Lyon", to: "/soiree-entreprise-lyon" },
              { label: "Notre agence événementielle à Lyon", to: "/agence-evenementielle-entreprise-lyon" },
            ].map((r) => (
              <Link key={r.to} to={r.to} className="group flex items-center gap-2 rounded-full border border-ink/15 bg-mist px-5 py-2.5 text-sm text-ink/70 transition-colors hover:border-lime-dim hover:text-ink" data-testid={`noel-lyon-related-${r.to.replace(/\//g, "")}`}>
                {r.label} <ArrowUpRight size={14} className="text-lime-dim" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28" data-testid="noel-lyon-cta">
        <Reveal><h2 className="max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-5xl">Votre soirée de Noël, <span className="italic text-lime-dim">sans le stress.</span></h2></Reveal>
        <Reveal delay={1}>
          <p className="mt-5 flex items-center gap-2 text-sm text-ink/55"><Clock size={15} className="text-lime-dim" /> Date, effectif, lieu, budget : le formulaire prend 2 minutes, la première réponse arrive sous 24 h ouvrées.</p>
        </Reveal>
        <Reveal delay={2}>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/devis" onClick={() => track("click_devis")} className={btnPrimary} data-testid="noel-lyon-devis-btn">Recevoir une première proposition <ArrowRight size={16} /></Link>
            <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} onClick={() => track("click_phone")} className={btnOutline}>Appeler le {CONTACT.phoneMobile}</a>
          </div>
        </Reveal>
      </section>
    </>
  );
}
